import React, { FC, useState } from 'react';

import { Button, Col, Row } from 'antd';
import routePaths from 'config/routes';
import { FormattedMessage } from 'react-intl';
import { Dispatch } from 'redux';
import * as api from 'services/api';
import apiPaths from 'services/apiPaths';
import { UpdateUserPoints } from 'src/actions/authActions';
import CustomModal from 'src/components/CustomModal';
import Link from 'src/components/Link';
import Question from 'src/components/quiz/Question';
import { IChallenge, IChallengeQuiz } from 'src/shared/models';

// TODO: Encapsulate into interface "IBasicResourceProps"
interface IProps {
  config: IChallengeQuiz;
  handleShowQuiz: React.Dispatch<React.SetStateAction<boolean>>;
  updateChallenge: React.Dispatch<React.SetStateAction<IChallenge>>;
  updateUserPoints: (
    userPoints: number
  ) => (dispatch: Dispatch<UpdateUserPoints>) => void;
}

const Quiz: FC<IProps> = ({
  config,
  handleShowQuiz,
  updateChallenge,
  updateUserPoints,
}): JSX.Element => {
  const {
    idResource,
    questions,
    showStatistics,
    isQuizDone,
    completationTitle,
    allowContinue,
  } = config;
  const [savedAnswers, setSavedAnswers] = useState<number[]>([]);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState(null);
  const [pointsEarned, setPointsEarned] = useState(0);
  const [modalQuizDone, setModalQuizDone] = useState(isQuizDone);

  const showEarnedPoints =
    !modalQuizDone && pointsEarned !== 0 && pointsEarned !== null;

  const handleSavedAnswers = (idResourceAnswer: number) => {
    setSavedAnswers([...savedAnswers, idResourceAnswer]);
  };

  const closeModal = () => {
    setIsModalVisible(false);
    handleShowQuiz(false);
    setSavedAnswers([]);
    setError(null);
    setPointsEarned(0);
    setModalQuizDone(true);
  };

  const submitQuiz = async (idResourceAnswer: number) => {
    try {
      setIsModalVisible(true);
      setLoading(true);
      const response = await api.putDataCallById({
        dataPath: apiPaths.CHALLENGES.COMPLETE_TRAINING,
        id: idResource,
        data: { resourceList: [...savedAnswers, idResourceAnswer] },
      });

      const { score, challenge, trainingScore } = response.data;

      if (score && typeof score === 'number') updateUserPoints(score);
      if (trainingScore) setPointsEarned(Number(trainingScore));

      updateChallenge(challenge);
    } catch (err) {
      setError(err);
      console.error(err);
    } finally {
      setSavedAnswers([]);
      setLoading(false);
    }
  };

  const modalContent = (
    <Row justify="center" align="middle">
      <Col xs={24} md={14}>
        <Row justify="center" align="middle">
          <a className="icon icon__size-120 icon--process-check" />
        </Row>
        <Row justify="center" className="block--small">
          <span className="finalize-title">
            <FormattedMessage id={completationTitle} />
          </span>
        </Row>
        <Row className="block--small regular-body" justify="center">
          {showEarnedPoints && (
            <FormattedMessage
              id="page.challenge.quiz.you-win-{points}-puntos"
              values={{
                points: Number(Math.max(pointsEarned, 0)).toLocaleString(),
              }}
            />
          )}
        </Row>
        <Row>
          <Link href={routePaths.PAGES.TRAINING}>
            <Button
              className={'btn-primary btn-primary--black'}
              onClick={closeModal}
              block
            >
              <FormattedMessage id="page.challenge.quiz.see-more-trainings" />
            </Button>
          </Link>
        </Row>
      </Col>
    </Row>
  );

  const modalResult = (): JSX.Element => {
    return (
      <CustomModal
        centered={true}
        content={modalContent}
        customClass="quiz-modal-finalize"
        error={error}
        loading={loading}
        footer={null}
        onCancel={closeModal}
        showIcon={false}
        visible={isModalVisible}
      />
    );
  };

  return (
    <Row className="quiz-container" justify="center" align="middle">
      <Col xs={24} lg={20}>
        {questions.length && (
          <Question
            questions={questions}
            totalResponses={config.totalResponses}
            showStatistics={showStatistics}
            saveAnswer={handleSavedAnswers}
            handleSubmit={submitQuiz}
            allowContinue={allowContinue}
          />
        )}
      </Col>
      {modalResult()}
    </Row>
  );
};

export default Quiz;
